import { useCallback, useEffect, useState } from "react";

import { getHttpStatus } from "@src/shared/utils/http/errors";

interface CursorErrorState {
  currentCursor: string | undefined;
  isError: boolean;
  error: unknown;
}

export function isStaleCursorError({
  currentCursor,
  error,
  isError,
}: CursorErrorState): boolean {
  return isError && currentCursor != null && getHttpStatus(error) === 400;
}

export function shouldRetryCursorQueryError(
  failureCount: number,
  error: unknown,
): boolean {
  const status = getHttpStatus(error);
  if (status != null && status >= 400 && status < 500) return false;
  return failureCount < 2;
}

export function useResetStaleCursor(
  isStaleCursor: boolean,
  resetCursor: () => void,
) {
  useEffect(() => {
    if (isStaleCursor) resetCursor();
  }, [isStaleCursor, resetCursor]);
}

export function useCursorPagination() {
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const currentCursor = cursorStack.at(-1);

  const resetCursor = useCallback(() => {
    setCursorStack([]);
  }, []);

  const goToNextPage = useCallback((nextCursor: string | null | undefined) => {
    if (nextCursor) setCursorStack((stack) => [...stack, nextCursor]);
  }, []);

  const goToPreviousPage = useCallback(() => {
    setCursorStack((stack) => stack.slice(0, -1));
  }, []);

  return {
    currentCursor,
    cursorStack,
    goToNextPage,
    goToPreviousPage,
    hasPrevPage: cursorStack.length > 0,
    resetCursor,
  };
}
